import {
  BrickPattern,
  BrickSizes,
  brickSizes,
  Joint,
  JointSize,
  StructureType,
  structures,
} from "../constants";
import type { Brick, BrickSeries } from "../store/slices/bricksSlice";

// Bump on any change that moves or swaps a brick for existing inputs (see ADR 0001).
export const LAYOUT_VERSION = 1;

const MIN_TILE_MM = 1200;
const MAX_JOINT_MM = JointSize.Large;

export interface WallLayoutConfig {
  brickSelection: Record<string, number>;
  brickSize: BrickSizes;
  structure: StructureType;
  jointSize: Record<Joint, number>;
}

export interface PlacedBrick {
  row: number;
  column: number;
  type: BrickPattern;
  seriesId: string;
  brick: Brick;
  mirrored: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WallTileLayout {
  version: number;
  brickSize: BrickSizes;
  structure: StructureType;
  seed: number;
  widthMm: number;
  heightMm: number;
  courses: number;
  bricks: PlacedBrick[];
}

const mix32 = (value: number) => {
  let x = value >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x7feb352d);
  x ^= x >>> 15;
  x = Math.imul(x, 0x846ca68b);
  x ^= x >>> 16;
  return x >>> 0;
};

enum Salt {
  Series,
  Brick,
  Mirror,
}

const hash01 = (seed: number, row: number, column: number, salt: Salt) =>
  mix32(mix32(mix32(mix32(seed) ^ row) ^ column) ^ salt) / 2 ** 32;

// Positions measured in half modules, where one module is a full brick plus one
// vertical joint: a full brick spans 2 units, a half brick 1.
const unitsOf = (type: BrickPattern) => (type === BrickPattern.Full ? 2 : 1);

const tileGrid = (brickSize: BrickSizes, structure: StructureType) => {
  const { width, height } = brickSizes[brickSize];
  const rowCount = structures[structure].rows.length;
  const modules = Math.ceil(MIN_TILE_MM / (width + MAX_JOINT_MM));
  const courses =
    rowCount * Math.ceil(MIN_TILE_MM / (height + MAX_JOINT_MM) / rowCount);
  return { units: modules * 2, courses };
};

// Rows cycle through their pattern until the tile width is filled. Patterns
// whose length doesn't divide the tile width (Wild) are cut off, with a half
// brick when only one unit remains.
const rowTypes = (pattern: BrickPattern[], units: number) => {
  const types: BrickPattern[] = [];
  let used = 0;
  for (let i = 0; used < units; i++) {
    const planned = pattern[i % pattern.length];
    const type = used + unitsOf(planned) > units ? BrickPattern.Half : planned;
    types.push(type);
    used += unitsOf(type);
  }
  return types;
};

const pickSeries = (
  selection: [string, number][],
  seed: number,
  row: number,
  column: number
) => {
  const r = hash01(seed, row, column, Salt.Series) * 100;
  let edge = 0;
  for (const [id, rate] of selection) {
    edge += rate;
    if (r < edge) return id;
  }
  return selection[selection.length - 1][0];
};

export const layoutWallTile = (
  config: WallLayoutConfig,
  seed: number,
  brickSeries: Record<string, BrickSeries>
): WallTileLayout => {
  const { width, height } = brickSizes[config.brickSize];
  const structure = structures[config.structure];
  const vertical = config.jointSize[Joint.Vertical];
  const horizontal = config.jointSize[Joint.Horizontal];
  const { units, courses } = tileGrid(config.brickSize, config.structure);
  const unitMm = (width + vertical) / 2;

  const selection = Object.entries(config.brickSelection)
    .filter(([id]) => (brickSeries[id]?.bricks.length ?? 0) > 0)
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));

  const bricks: PlacedBrick[] = [];
  if (selection.length > 0) {
    for (let row = 0; row < courses; row++) {
      const { pattern, margin = 0 } = structure.rows[row % structure.rows.length];
      const types = rowTypes(pattern, units);
      let x = margin * unitsOf(types[0]) * unitMm + vertical / 2;
      const y = row * (height + horizontal) + horizontal / 2;

      types.forEach((type, column) => {
        const seriesId = pickSeries(selection, seed, row, column);
        const all = brickSeries[seriesId].bricks;
        const ofType = all.filter((b) => b.type === type);
        const candidates = ofType.length > 0 ? ofType : all;
        const index = Math.floor(
          hash01(seed, row, column, Salt.Brick) * candidates.length
        );
        const span = unitsOf(type) * unitMm;
        bricks.push({
          row,
          column,
          type,
          seriesId,
          brick: candidates[index],
          mirrored: hash01(seed, row, column, Salt.Mirror) < 0.5,
          x,
          y,
          width: span - vertical,
          height,
        });
        x += span;
      });
    }
  }

  return {
    version: LAYOUT_VERSION,
    brickSize: config.brickSize,
    structure: config.structure,
    seed,
    widthMm: units * unitMm,
    heightMm: courses * (height + horizontal),
    courses,
    bricks,
  };
};
